import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * ClientDesk VA design primitives.
 * Rules live in `/app/design_guidelines.md`.
 */

/** Card container for grouped form / list content. */
export function WorkspaceCard({ className, children, ...rest }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-[#E8E2D9] bg-white p-5 space-y-4",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Tiny caps section header — sits above a WorkspaceCard body. */
export function SectionHeader({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#57534E] font-semibold">
        {Icon && <Icon size={13} strokeWidth={1.75} className="text-[#57534E]" />}
        <span>{title}</span>
        {hint && <span className="text-[#A8A29E] normal-case tracking-normal font-normal ml-1">{hint}</span>}
      </div>
      {action}
    </div>
  );
}

/** Standard labeled form field. Always use this for new fields. */
export function FormField({ label, required, help, hint, wide, htmlFor, children, className }) {
  return (
    <div className={cn(wide && "md:col-span-2", className)}>
      <div className="flex items-baseline justify-between">
        <Label htmlFor={htmlFor} className="text-sm font-medium text-[#1C1917]">
          {label}
          {required && <span className="text-[#E8712C]"> *</span>}
        </Label>
        {hint && <span className="text-[11px] text-[#A8A29E]">{hint}</span>}
      </div>
      <div className="mt-1.5">{children}</div>
      {help && <p className="text-xs text-[#57534E] mt-1.5">{help}</p>}
    </div>
  );
}

/** Empty state block. */
export function EmptyState({ icon: Icon, title, description, action, className, testid }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-dashed border-[#E8E2D9] bg-white p-10 md:p-16 text-center flex flex-col items-center justify-center",
        className
      )}
      data-testid={testid}
    >
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-[#FDE9D8] text-[#E8712C] flex items-center justify-center mb-4">
          <Icon size={22} strokeWidth={1.5} />
        </div>
      )}
      {title && <h3 className="font-display font-semibold text-lg tracking-tight">{title}</h3>}
      {description && <p className="text-sm text-[#57534E] mt-1 max-w-md">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Primary orange rounded button. */
export function PrimaryButton({ className, ...rest }) {
  return (
    <Button
      className={cn("rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white", className)}
      {...rest}
    />
  );
}

/** Secondary outline button. */
export function SecondaryButton({ className, ...rest }) {
  return <Button variant="outline" className={cn("rounded-full", className)} {...rest} />;
}

/** Destructive outline button. */
export function DestructiveButton({ className, ...rest }) {
  return (
    <Button
      variant="outline"
      className={cn(
        "rounded-full text-[#B02222] border-[#F5C6C6] hover:bg-[#FDECEC]",
        className
      )}
      {...rest}
    />
  );
}

/** Sticky action bar for mobile workspace overlay OR bottom of long forms. */
export function ActionBar({ children, className }) {
  return (
    <div
      className={cn(
        "flex gap-2 flex-wrap items-center justify-end border-t border-[#E8E2D9] pt-4 mt-4",
        className
      )}
    >
      {children}
    </div>
  );
}
